import type { SupportedLocale } from "@repo/core";

export interface QuickStartSection {
  heading: string;
  bullets: string[];
}

export interface QuickStartGuide {
  /** Basename without extension, e.g. BudgetOS-Inicio-Rapido */
  fileBase: string;
  lang: SupportedLocale;
  title: string;
  subtitle: string;
  openAppLabel: string;
  openAppHint: string;
  printLabel: string;
  openPdfLabel: string;
  warnTitle: string;
  warnBody: string;
  sections: QuickStartSection[];
}

/** Localized Quick-Start filenames + copy (one guide per Etsy language zip). */
export const QUICKSTART_GUIDES: Record<SupportedLocale, QuickStartGuide> = {
  en: {
    fileBase: "BudgetOS-Quick-Start",
    lang: "en",
    title: "BudgetOS Quick Start",
    subtitle:
      "A short guide for non-technical users. Keep this file next to index.html in your download folder.",
    openAppLabel: "Open BudgetOS (index.html)",
    openAppHint: "Opens the app in a new tab. Leave this guide open for reference.",
    printLabel: "Print / Save as PDF",
    openPdfLabel: "Open Quick-Start PDF",
    warnTitle: "Important (Safari / file download users)",
    warnBody:
      "After you’ve saved real data, export a backup before renaming or moving the BudgetOS HTML folder. Some browsers store data based on the file’s path.",
    sections: [
      {
        heading: "Where your data lives",
        bullets: [
          "Everything is saved on this device, in this browser — not in the cloud.",
          "No account. No subscription. No internet needed after the file loads.",
        ],
      },
      {
        heading: "First 5 minutes",
        bullets: [
          "On first open, choose sample data or start empty (default categories only).",
          "Use the + button to add income or expenses.",
          "Open Settings to set your currency and theme.",
          "Optional later: Settings → Load Demo Data to explore with realistic numbers.",
          "Tap ? anytime for Help & FAQ.",
        ],
      },
      {
        heading: "Backup (please do this)",
        bullets: [
          "Settings → Backup & Restore → download your JSON backup.",
          "To move to a new phone/computer/browser: Import that file there.",
          "Clearing browser data will erase BudgetOS unless you have a backup.",
        ],
      },
      {
        heading: "Reset",
        bullets: [
          "Settings → Clear All Data removes transactions, accounts, debts, and funds. Default categories stay so you can keep budgeting immediately.",
          "To wipe custom categories too, use Settings → Categories → Restore Default Categories.",
        ],
      },
      {
        heading: "Offline & privacy",
        bullets: [
          "Works fully offline.",
          "No analytics or tracking scripts in the product build.",
        ],
      },
      {
        heading: "PDF & chart exports",
        bullets: [
          "Dashboard / Monthly Budget / Settings → Download PDF creates a branded Annual Report.",
          "Charts can export as PNG with BudgetOS colors for sharing.",
        ],
      },
    ],
  },

  es: {
    fileBase: "BudgetOS-Inicio-Rapido",
    lang: "es",
    title: "BudgetOS — Inicio rápido",
    subtitle:
      "Guía corta para usuarios no técnicos. Deja este archivo junto a index.html en la carpeta descargada.",
    openAppLabel: "Abrir BudgetOS (index.html)",
    openAppHint: "Abre la app en una pestaña nueva. Puedes dejar esta guía abierta.",
    printLabel: "Imprimir / Guardar como PDF",
    openPdfLabel: "Abrir PDF de inicio rápido",
    warnTitle: "Importante (Safari / descargas locales)",
    warnBody:
      "Cuando ya tengas datos reales, exporta una copia de seguridad antes de renombrar o mover la carpeta de BudgetOS. Algunos navegadores asocian los datos a la ruta del archivo.",
    sections: [
      {
        heading: "Dónde viven tus datos",
        bullets: [
          "Todo se guarda en este dispositivo y en este navegador — no en la nube.",
          "Sin cuenta. Sin suscripción. Sin internet después de cargar el archivo.",
        ],
      },
      {
        heading: "Primeros 5 minutos",
        bullets: [
          "Al abrir por primera vez, elige datos de ejemplo o empezar vacío (solo categorías por defecto).",
          "Usa el botón + para añadir ingresos o gastos.",
          "Abre Ajustes para moneda y tema.",
          "Opcional después: Ajustes → Cargar datos de demo.",
          "Pulsa ? cuando quieras para Ayuda y FAQ.",
        ],
      },
      {
        heading: "Copia de seguridad (hazlo)",
        bullets: [
          "Ajustes → Copia y restauración → descarga tu JSON.",
          "Para cambiar de móvil/ordenador/navegador: importa ese archivo allí.",
          "Borrar datos del navegador elimina BudgetOS si no tienes copia.",
        ],
      },
      {
        heading: "Reiniciar",
        bullets: [
          "Ajustes → Borrar todos los datos quita movimientos, cuentas, deudas y fondos. Las categorías por defecto se quedan.",
          "Para resetear también categorías personalizadas: Ajustes → Categorías → Restaurar categorías por defecto.",
        ],
      },
      {
        heading: "Offline y privacidad",
        bullets: [
          "Funciona totalmente sin conexión.",
          "Sin analytics ni scripts de seguimiento en la versión de producto.",
        ],
      },
      {
        heading: "PDF y gráficos",
        bullets: [
          "Panel / Presupuesto mensual / Ajustes → Descargar PDF crea el informe anual.",
          "Los gráficos se pueden exportar como PNG con los colores de BudgetOS.",
        ],
      },
    ],
  },

  fr: {
    fileBase: "BudgetOS-Demarrage-Rapide",
    lang: "fr",
    title: "BudgetOS — Démarrage rapide",
    subtitle:
      "Guide court pour utilisateurs non techniques. Gardez ce fichier à côté de index.html.",
    openAppLabel: "Ouvrir BudgetOS (index.html)",
    openAppHint: "Ouvre l’app dans un nouvel onglet. Vous pouvez garder ce guide ouvert.",
    printLabel: "Imprimer / Enregistrer en PDF",
    openPdfLabel: "Ouvrir le PDF de démarrage",
    warnTitle: "Important (Safari / fichiers locaux)",
    warnBody:
      "Après avoir enregistré de vraies données, exportez une sauvegarde avant de renommer ou déplacer le dossier BudgetOS. Certains navigateurs lient le stockage au chemin du fichier.",
    sections: [
      {
        heading: "Où vivent vos données",
        bullets: [
          "Tout est enregistré sur cet appareil, dans ce navigateur — pas dans le cloud.",
          "Pas de compte. Pas d’abonnement. Pas d’internet une fois le fichier chargé.",
        ],
      },
      {
        heading: "Les 5 premières minutes",
        bullets: [
          "Au premier lancement, choisissez données d’exemple ou démarrer vide (catégories par défaut uniquement).",
          "Utilisez le bouton + pour ajouter revenus ou dépenses.",
          "Ouvrez Réglages pour la devise et le thème.",
          "Plus tard (optionnel) : Réglages → Charger les données de démo.",
          "Appuyez sur ? pour l’aide et la FAQ.",
        ],
      },
      {
        heading: "Sauvegarde (faites-le)",
        bullets: [
          "Réglages → Sauvegarde → téléchargez votre JSON.",
          "Pour changer d’appareil/navigateur : importez ce fichier là-bas.",
          "Effacer les données du navigateur efface BudgetOS sans sauvegarde.",
        ],
      },
      {
        heading: "Réinitialiser",
        bullets: [
          "Réglages → Tout effacer retire transactions, comptes, dettes et fonds. Les catégories par défaut restent.",
          "Pour aussi réinitialiser les catégories perso : Réglages → Catégories → Restaurer les catégories par défaut.",
        ],
      },
      {
        heading: "Hors ligne & confidentialité",
        bullets: [
          "Fonctionne entièrement hors ligne.",
          "Pas d’analytics ni de scripts de suivi dans la version produit.",
        ],
      },
      {
        heading: "PDF & graphiques",
        bullets: [
          "Tableau de bord / Budget mensuel / Réglages → Télécharger le PDF crée le rapport annuel.",
          "Les graphiques s’exportent en PNG aux couleurs BudgetOS.",
        ],
      },
    ],
  },

  de: {
    fileBase: "BudgetOS-Schnellstart",
    lang: "de",
    title: "BudgetOS — Schnellstart",
    subtitle:
      "Kurzer Leitfaden für Einsteiger. Lassen Sie diese Datei neben index.html im Download-Ordner.",
    openAppLabel: "BudgetOS öffnen (index.html)",
    openAppHint: "Öffnet die App in einem neuen Tab. Dieser Leitfaden kann offen bleiben.",
    printLabel: "Drucken / Als PDF speichern",
    openPdfLabel: "Schnellstart-PDF öffnen",
    warnTitle: "Wichtig (Safari / lokale Dateien)",
    warnBody:
      "Exportieren Sie ein Backup, bevor Sie den BudgetOS-Ordner umbenennen oder verschieben. Manche Browser speichern Daten pfadbezogen.",
    sections: [
      {
        heading: "Wo Ihre Daten liegen",
        bullets: [
          "Alles wird auf diesem Gerät in diesem Browser gespeichert — nicht in der Cloud.",
          "Kein Konto. Kein Abo. Kein Internet nach dem Laden der Datei.",
        ],
      },
      {
        heading: "Die ersten 5 Minuten",
        bullets: [
          "Beim ersten Start: Beispieldaten oder leer starten (nur Standardkategorien).",
          "Mit + Einnahmen oder Ausgaben hinzufügen.",
          "Unter Einstellungen Währung und Design setzen.",
          "Später optional: Einstellungen → Demo-Daten laden.",
          "Tippen Sie auf ? für Hilfe & FAQ.",
        ],
      },
      {
        heading: "Backup (bitte machen)",
        bullets: [
          "Einstellungen → Backup → JSON herunterladen.",
          "Für ein neues Gerät/Browser: dort importieren.",
          "Browserdaten löschen entfernt BudgetOS ohne Backup.",
        ],
      },
      {
        heading: "Zurücksetzen",
        bullets: [
          "Einstellungen → Alle Daten löschen entfernt Transaktionen, Konten, Schulden und Fonds. Standardkategorien bleiben.",
          "Eigene Kategorien zurücksetzen: Einstellungen → Kategorien → Standard wiederherstellen.",
        ],
      },
      {
        heading: "Offline & Privatsphäre",
        bullets: [
          "Voll offline nutzbar.",
          "Keine Analytics oder Tracking-Skripte in der Produktversion.",
        ],
      },
      {
        heading: "PDF & Diagramme",
        bullets: [
          "Dashboard / Monatsbudget / Einstellungen → PDF lädt den Jahresbericht.",
          "Diagramme als PNG mit BudgetOS-Farben exportierbar.",
        ],
      },
    ],
  },

  pt: {
    fileBase: "BudgetOS-Guia-Rapido",
    lang: "pt",
    title: "BudgetOS — Guia rápido",
    subtitle:
      "Guia curto para utilizadores não técnicos. Mantenha este ficheiro junto de index.html.",
    openAppLabel: "Abrir BudgetOS (index.html)",
    openAppHint: "Abre a app num novo separador. Pode deixar este guia aberto.",
    printLabel: "Imprimir / Guardar como PDF",
    openPdfLabel: "Abrir PDF do guia rápido",
    warnTitle: "Importante (Safari / ficheiros locais)",
    warnBody:
      "Depois de guardar dados reais, exporte uma cópia de segurança antes de renomear ou mover a pasta do BudgetOS. Alguns browsers associam os dados ao caminho do ficheiro.",
    sections: [
      {
        heading: "Onde ficam os seus dados",
        bullets: [
          "Tudo é guardado neste dispositivo e neste browser — não na cloud.",
          "Sem conta. Sem subscrição. Sem internet depois de carregar o ficheiro.",
        ],
      },
      {
        heading: "Primeiros 5 minutos",
        bullets: [
          "Na primeira abertura, escolha dados de exemplo ou começar vazio (só categorias predefinidas).",
          "Use o botão + para adicionar receitas ou despesas.",
          "Abra Definições para moeda e tema.",
          "Mais tarde (opcional): Definições → Carregar dados de demonstração.",
          "Toque em ? para Ajuda e FAQ.",
        ],
      },
      {
        heading: "Cópia de segurança (faça isto)",
        bullets: [
          "Definições → Cópia e restauro → descarregue o JSON.",
          "Para mudar de telemóvel/computador/browser: importe esse ficheiro lá.",
          "Limpar dados do browser apaga o BudgetOS sem cópia.",
        ],
      },
      {
        heading: "Repor",
        bullets: [
          "Definições → Apagar todos os dados remove movimentos, contas, dívidas e fundos. As categorias predefinidas ficam.",
          "Para repor categorias personalizadas: Definições → Categorias → Restaurar categorias predefinidas.",
        ],
      },
      {
        heading: "Offline e privacidade",
        bullets: [
          "Funciona totalmente offline.",
          "Sem analytics nem scripts de tracking na versão de produto.",
        ],
      },
      {
        heading: "PDF e gráficos",
        bullets: [
          "Painel / Orçamento mensal / Definições → Descarregar PDF cria o relatório anual.",
          "Os gráficos podem exportar-se como PNG com as cores do BudgetOS.",
        ],
      },
    ],
  },

  it: {
    fileBase: "BudgetOS-Guida-Rapida",
    lang: "it",
    title: "BudgetOS — Guida rapida",
    subtitle:
      "Guida breve per utenti non tecnici. Tieni questo file accanto a index.html.",
    openAppLabel: "Apri BudgetOS (index.html)",
    openAppHint: "Apre l’app in una nuova scheda. Puoi lasciare aperta questa guida.",
    printLabel: "Stampa / Salva come PDF",
    openPdfLabel: "Apri PDF guida rapida",
    warnTitle: "Importante (Safari / file locali)",
    warnBody:
      "Dopo aver salvato dati reali, esporta un backup prima di rinominare o spostare la cartella di BudgetOS. Alcuni browser collegano i dati al percorso del file.",
    sections: [
      {
        heading: "Dove restano i tuoi dati",
        bullets: [
          "Tutto è salvato su questo dispositivo, in questo browser — non nel cloud.",
          "Nessun account. Nessun abbonamento. Niente internet dopo il caricamento del file.",
        ],
      },
      {
        heading: "I primi 5 minuti",
        bullets: [
          "Al primo avvio scegli dati di esempio o partire vuoto (solo categorie predefinite).",
          "Usa il pulsante + per aggiungere entrate o uscite.",
          "Apri Impostazioni per valuta e tema.",
          "Più tardi (opzionale): Impostazioni → Carica dati demo.",
          "Tocca ? per Aiuto e FAQ.",
        ],
      },
      {
        heading: "Backup (fallo)",
        bullets: [
          "Impostazioni → Backup → scarica il JSON.",
          "Per cambiare telefono/computer/browser: importa quel file lì.",
          "Cancellare i dati del browser elimina BudgetOS senza backup.",
        ],
      },
      {
        heading: "Reset",
        bullets: [
          "Impostazioni → Cancella tutti i dati rimuove movimenti, conti, debiti e fondi. Restano le categorie predefinite.",
          "Per ripristinare anche le categorie personalizzate: Impostazioni → Categorie → Ripristina categorie predefinite.",
        ],
      },
      {
        heading: "Offline e privacy",
        bullets: [
          "Funziona completamente offline.",
          "Niente analytics né script di tracciamento nella build di prodotto.",
        ],
      },
      {
        heading: "PDF e grafici",
        bullets: [
          "Dashboard / Budget mensile / Impostazioni → Scarica PDF crea il report annuale.",
          "I grafici si esportano in PNG con i colori BudgetOS.",
        ],
      },
    ],
  },
};

export function quickStartFiles(locale: SupportedLocale): { html: string; pdf: string; base: string } {
  const base = QUICKSTART_GUIDES[locale].fileBase;
  return { base, html: `${base}.html`, pdf: `${base}.pdf` };
}
